import {ExternalAppealPatientModel} from "@/models/external_appeals/ExternalAppealPatientModel.js";
import {ExternalAppealHealthPlanModel} from "@/models/external_appeals/ExternalAppealHealthPlanModel.js";
import {ExternalAppealAuthorizedRepModel} from "@/models/external_appeals/ExternalAppealAuthorizedRepModel.js";
import {ExternalAppealProviderModel} from "@/models/external_appeals/ExternalAppealProviderModel.js";
import {ExternalAppealModel} from "@/models/external_appeals/ExternalAppealModel.js";
import {ExternalAppealDocumentModel} from "@/models/external_appeals/ExternalAppealDocumentModel.js";
import axios from "axios";
import {checkToken} from "@/utils/lambdaUtils.js";
import {useAuthStore} from "@/stores/authStore.js";
import logger from "@/utils/logger.js";
import {EXTERNAL_APPEALS_STATUSES} from "@/utils/constants.js";

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL + '/external_appeals/';

export const submitAppeal = async (formData) => {
    try {
        // Instantiate nested models first
        const patient = new ExternalAppealPatientModel({
            firstName: formData.patient.firstName,
            middleName: formData.patient.middleName || null,
            lastName: formData.patient.lastName,
            dob: formData.patient.dob,
            phoneNumber: formData.patient.phoneNumber,
            email: formData.patient.email,
            street: formData.patient.street,
            city: formData.patient.city,
            state: formData.patient.state,
            zipCode: formData.patient.zipCode,
            relationshipToPatient: formData.patient.relationshipToPatient,
            otherRelationship: formData.patient.otherRelationship || null,
        });

        const healthPlan = new ExternalAppealHealthPlanModel({
            healthInsuranceCompany: formData.healthPlan.healthInsuranceCompany,
            otherHmoName: formData.healthPlan.otherHmoName || null,
            patientInsuranceId: formData.healthPlan.patientInsuranceId,
            typeOfCoverage: formData.healthPlan.typeOfCoverage || null,
            otherTypeOfCoverage: formData.healthPlan.otherTypeOfCoverage,
        });

        const documents = formData.documents.map(doc =>
            new ExternalAppealDocumentModel({
                type: doc.type,
                url: doc.url
            })
        );

        const authorizedRep = formData.authorizedRep
            ? new ExternalAppealAuthorizedRepModel({
                fullName: formData.authorizedRep.fullName,
                phoneNumber: formData.authorizedRep.phoneNumber,
                email: formData.authorizedRep.email
            })
            : null;

        const provider = formData.provider
            ? new ExternalAppealProviderModel({
                name: formData.provider.name,
                street: formData.provider.street,
                city: formData.provider.city,
                state: formData.provider.state,
                zipCode: formData.provider.zipCode,
                representative: formData.provider.representative,
                phoneNumber: formData.provider.phoneNumber,
                email: formData.provider.email
            })
            : null;

        // Now build the main appeal model, linking nested models
        const appeal = new ExternalAppealModel({
            appealStatus: EXTERNAL_APPEALS_STATUSES.InfoNeeded,
            appealOutcome: formData.appealOutcome || null,
            appealCategory: formData.appealCategory || null,
            isExpedited: formData.isExpedited,
            isExternalAppealOnly: formData.isExternalAppealOnly,
            isForwardingAuthorized: formData.isForwardingAuthorized === 'Authorized',
            expeditedReason: formData.expeditedReason || null,
            summaryOfAppeal: formData.summaryOfAppeal,
            internalAppealStatus: formData.internalAppealStatus || null,
            internalAppealDenialDate: formData.internalAppealDenialDate || null,
            signature: formData.signature,
            signatureDate: Date.now(),
            signatureRelationship: formData.signatureRelationship,
            reviewOrganization: formData.reviewOrganization || null,
            assignedInvestigator: formData.assignedInvestigator || null,
            iroAt: formData.iroAt || null,

            patient,
            healthPlan,
            documents,
            authorizedRep,
            provider
        });

        // Prepare plain JSON payload for axios (strip methods, keep data)
        const payload = appeal.toRequestPayload();

        // POST to API
        const response = await axios.post(`${baseUrl}`, payload, {
            headers: {
                'Content-Type': 'application/json'
            }
        });

        console.log('Appeal submitted:', response.data);
        return response.data;

    } catch (e) {
        console.error('Error submitting appeal:', e);
        throw e;
    }
};


export const getExternalAppealById = async (appealID) => {
    const numId = Number.parseInt(appealID, 10);
    try {
        await checkToken();
        const store = useAuthStore();
        const response = await axios.get(`${baseUrl}${encodeURIComponent(numId)}`, {
            headers: {
                'Authorization': `Bearer ${store.token}`,
            }
        });
        if (response.data) {
            return new ExternalAppealModel(response.data);
        } else {
            console.log(response);
            throw new Error('Invalid response data format');
        }
    } catch (error) {
        logger.error('Error getting external appeals', error);
    }
};

export const appealStatusCheck = async (payload) => {
    return await axios.get(`${baseUrl}status_check`, {
        params: payload
    });
};
